import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MapPin, Phone, Clock, Navigation, Search, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

// Coordonnées exactes du siège : 5 rue Mouflette, Méru 60110
// N= 49. 14. 9,38 ; E=2. 8. 8,369
const MERU_COORDS = { lat: 49.23593898729486, lng: 2.1356582871690932 };

// Rayon d'intervention en km
const INTERVENTION_RADIUS_KM = 50;

// Liste des villes principales dans la zone avec coordonnées (multi-départements pour SEO)
const MAIN_CITIES = [
  // 60 - Oise
  { name: 'Méru', isHQ: true, lat: 49.2359, lng: 2.1357, dept: '60' },
  { name: 'Beauvais', lat: 49.4298, lng: 2.0800, dept: '60' },
  { name: 'Senlis', lat: 49.2066, lng: 2.5866, dept: '60' },
  // 75 - Paris
  { name: 'Paris', lat: 48.8566, lng: 2.3522, dept: '75' },
  // 95 - Val-d'Oise
  { name: 'Cergy', lat: 49.0364, lng: 2.0636, dept: '95' },
  // 93 - Seine-Saint-Denis
  { name: 'Saint-Denis', lat: 48.9362, lng: 2.3574, dept: '93' },
  // 92 - Hauts-de-Seine
  { name: 'Nanterre', lat: 48.8924, lng: 2.2071, dept: '92' },
];

// Calcul de distance entre deux points (formule de Haversine)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Rayon de la Terre en km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

export const InteractiveMap = () => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCity, setSelectedCity] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const searchMarkerRef = useRef(null);
  
  // Fly to city on map
  const flyToCity = useCallback((city) => {
    if (mapInstanceRef.current && city.lat && city.lng) {
      mapInstanceRef.current.flyTo([city.lat, city.lng], city.isHQ ? 12 : 11, {
        duration: 1.5
      });
      setSelectedCity(city.name);
      setSearchResult(null);
    }
  }, []);
  
  // Search for any city in France using API
  const searchCity = useCallback(async () => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    setSearchResult(null);
    
    try {
      // Use French government API for address search
      const response = await fetch(
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(searchQuery)}&type=municipality&limit=1`
      );
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const feature = data.features[0];
        const [lng, lat] = feature.geometry.coordinates;
        const cityName = feature.properties.city || feature.properties.name;
        const postcode = feature.properties.postcode;
        
        // Calculate distance from Méru
        const distance = calculateDistance(MERU_COORDS.lat, MERU_COORDS.lng, lat, lng);
        const isInZone = distance <= INTERVENTION_RADIUS_KM;
        
        setSearchResult({
          name: cityName,
          postcode,
          lat,
          lng,
          distance: Math.round(distance),
          isInZone
        });
        
        // Fly to the searched city
        if (mapInstanceRef.current) {
          // Remove previous search marker
          if (searchMarkerRef.current) {
            searchMarkerRef.current.remove();
          }
          
          mapInstanceRef.current.flyTo([lat, lng], 11, { duration: 1.5 });
          
          // Add marker for searched city
          const L = window.L;
          if (L) {
            const markerColor = isInZone ? '#22c55e' : '#ef4444';
            const searchIcon = L.divIcon({
              className: 'custom-marker',
              html: `<div style="
                background: ${markerColor};
                width: 30px;
                height: 30px;
                border-radius: 50%;
                border: 3px solid white;
                box-shadow: 0 2px 10px rgba(0,0,0,0.3);
                display: flex;
                align-items: center;
                justify-content: center;
              ">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3" fill="${markerColor}"/>
                </svg>
              </div>`,
              iconSize: [30, 30],
              iconAnchor: [15, 15]
            });
            
            searchMarkerRef.current = L.marker([lat, lng], { icon: searchIcon })
              .addTo(mapInstanceRef.current)
              .bindPopup(`<strong>${cityName}</strong><br>${postcode}<br>${Math.round(distance)} km de Méru`)
              .openPopup();
          }
        }
      } else {
        setSearchResult({ notFound: true, query: searchQuery });
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResult({ error: true });
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery]);
  
  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      searchCity();
    }
  };
  
  // Filter main cities based on search (for quick access)
  const filteredCities = MAIN_CITIES.filter(city => 
    city.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    const loadLeaflet = () => {
      return new Promise((resolve, reject) => {
        if (window.L) {
          resolve(window.L);
          return;
        }

        const cssLink = document.createElement('link');
        cssLink.rel = 'stylesheet';
        cssLink.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        cssLink.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
        cssLink.crossOrigin = '';
        document.head.appendChild(cssLink);

        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
        script.crossOrigin = '';
        script.async = true;
        script.onload = () => resolve(window.L);
        script.onerror = reject;
        document.body.appendChild(script);
      });
    };

    const initMap = async () => {
      try {
        const L = await loadLeaflet();
        
        if (!mapRef.current || mapInstanceRef.current) return;

        // Créer la carte centrée sur Méru avec zoom approprié
        const map = L.map(mapRef.current, {
          center: [MERU_COORDS.lat, MERU_COORDS.lng],
          zoom: 9,
          scrollWheelZoom: false,
          zoomControl: true
        });

        mapInstanceRef.current = map;

        // Tuiles OpenStreetMap
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; OpenStreetMap'
        }).addTo(map);

        // Zone d'intervention 50km - Bleu charte graphique #3B80CE
        L.circle([MERU_COORDS.lat, MERU_COORDS.lng], {
          radius: 50000,
          color: '#3B80CE',
          fillColor: '#3B80CE',
          fillOpacity: 0.25,
          weight: 3
        }).addTo(map);

        // Marqueur Méru - Pin bleu taille réduite
        const meruIcon = L.divIcon({
          className: 'meru-marker',
          html: `
            <div style="position: relative; width: 36px; height: 46px;">
              <svg viewBox="0 0 24 32" style="width: 36px; height: 46px; filter: drop-shadow(0 3px 6px rgba(0,0,0,0.3));">
                <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20c0-6.6-5.4-12-12-12z" fill="#3B80CE"/>
                <circle cx="12" cy="11" r="5" fill="#F8FAF9"/>
              </svg>
            </div>
          `,
          iconSize: [36, 46],
          iconAnchor: [18, 46],
          popupAnchor: [0, -46]
        });

        L.marker([MERU_COORDS.lat, MERU_COORDS.lng], { icon: meruIcon })
          .addTo(map)
          .bindPopup(`
            <div style="font-family: Poppins, sans-serif; text-align: center; padding: 10px;">
              <strong style="color: #3B82F6; font-size: 18px;">MÉRU</strong>
              <br/>
              <span style="color: #0A2320; font-size: 13px; font-weight: 600;">Siège social</span>
              <br/>
              <span style="color: #666; font-size: 11px;">Zone d'intervention : 50 km</span>
            </div>
          `);

        setIsLoading(false);
      } catch (error) {
        console.error('Error loading map:', error);
        setIsLoading(false);
      }
    };

    initMap();

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <section id="zone" className="py-20 bg-[#F8FAF9] font-['Poppins']">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-[#153254] mb-4">
            Notre zone d'intervention
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Nous intervenons dans un rayon de <strong>50 km autour de Méru</strong>, 
            couvrant une grande partie de l'Oise et des départements limitrophes.
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Carte - Plus grande */}
            <div className="lg:col-span-2 relative">
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-2xl z-10">
                  <div className="text-center">
                    <div className="w-10 h-10 border-4 border-[#3B82F6] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                    <p className="text-gray-600">Chargement de la carte...</p>
                  </div>
                </div>
              )}
              <div 
                ref={mapRef} 
                className="w-full h-[450px] md:h-[550px] rounded-2xl shadow-xl overflow-hidden border-4 border-white"
                style={{ zIndex: 1 }}
              />
            </div>

            {/* Infos */}
            <div className="space-y-6">
              {/* Contact rapide */}
              <div className="bg-gradient-to-br from-[#0A2320] to-[#153254] rounded-2xl p-6 text-[#F8FAF9]">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-[#F8FAF9]">
                  <MapPin className="h-5 w-5 text-[#F8FAF9]" />
                  Notre siège
                </h3>
                <p className="text-[#F8FAF9]/80 mb-4">
                  5 rue Mouflette<br />
                  60110 Méru - Oise<br />
                  Hauts-de-France
                </p>
                <div className="space-y-3">
                  <a href="tel:0981543842" className="flex items-center gap-3 text-[#F8FAF9] hover:text-[#D4AF37] transition-colors">
                    <Phone className="h-5 w-5" />
                    09 81 54 38 42
                  </a>
                  <div className="flex items-start gap-3 text-[#F8FAF9]/80 text-sm">
                    <Clock className="h-5 w-5 flex-shrink-0 mt-0.5" />
                    <div>
                      <p>Lun, Mar, Jeu, Ven : 9h-14h / 16h-19h</p>
                      <p>Mer, Sam : 9h-16h</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Villes principales avec interactivité */}
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-lg font-bold text-[#153254] mb-4 flex items-center gap-2">
                  <Search className="h-5 w-5" />
                  Vérifiez votre ville
                </h3>
                
                {/* Search input with button */}
                <div className="mb-4">
                  <div className="flex gap-2">
                    <input 
                      type="text"
                      placeholder="Tapez le nom d'une ville..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={handleKeyPress}
                      className="flex-1 px-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B80CE] focus:border-transparent transition-all"
                    />
                    <button
                      onClick={searchCity}
                      disabled={isSearching || !searchQuery.trim()}
                      className="px-4 py-2 bg-[#0A2320] text-white rounded-xl hover:bg-[#153254] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                
                {/* Search result message */}
                {searchResult && !searchResult.notFound && !searchResult.error && (
                  <div className={`p-4 rounded-xl mb-4 ${
                    searchResult.isInZone 
                      ? 'bg-green-50 border border-green-200' 
                      : 'bg-orange-50 border border-orange-200'
                  }`}>
                    {searchResult.isInZone ? (
                      <div className="flex items-start gap-3">
                        <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-green-800">
                            Bonne nouvelle !
                          </p>
                          <p className="text-green-700 text-sm">
                            <strong>{searchResult.name}</strong> ({searchResult.postcode}) se situe dans notre zone d'intervention, à seulement {searchResult.distance} km de Méru.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-3">
                        <XCircle className="h-6 w-6 text-orange-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-orange-800">
                            Zone non couverte
                          </p>
                          <p className="text-orange-700 text-sm">
                            Désolé, <strong>{searchResult.name}</strong> ({searchResult.postcode}) se situe en dehors de notre zone d'intervention ({searchResult.distance} km de Méru).
                          </p>
                          <p className="text-orange-600 text-sm mt-2">
                            N'hésitez pas à nous contacter pour toute question !
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {searchResult && searchResult.notFound && (
                  <div className="p-4 rounded-xl mb-4 bg-gray-50 border border-gray-200">
                    <p className="text-gray-600 text-sm">
                      Aucune ville trouvée pour "<strong>{searchResult.query}</strong>". Vérifiez l'orthographe.
                    </p>
                  </div>
                )}
                
                {/* Quick access cities */}
                <p className="text-xs text-gray-500 mb-2">Villes principales desservies :</p>
                <div className="flex flex-wrap gap-2 max-h-[120px] overflow-y-auto">
                  {filteredCities.slice(0, 12).map(city => (
                    <button 
                      key={city.name}
                      onClick={() => flyToCity(city)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-300 ${
                        city.isHQ 
                          ? 'bg-[#3B80CE] text-[#F8FAF9] ring-2 ring-[#D4AF37]' 
                          : selectedCity === city.name
                            ? 'bg-[#D4AF37] text-white scale-105'
                            : 'bg-gray-100 text-gray-700 hover:bg-[#3B80CE] hover:text-[#F8FAF9]'
                      }`}
                    >
                      {city.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="bg-[#F8FAF9] border-2 border-[#0A2320] rounded-2xl p-6">
                <p className="text-[#153254] font-medium mb-4">
                  Vous n'êtes pas sûr d'être dans notre zone ?
                </p>
                <Button 
                  className="w-full bg-[#0A2320] hover:bg-[#0d2e2a] text-white rounded-xl"
                  onClick={() => window.location.href = 'tel:0981543842'}
                >
                  <Navigation className="mr-2 h-4 w-4" />
                  Appelez-nous pour vérifier
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
